/**
 * EmailJS Unified Service
 * Handles sending form notifications via EmailJS REST API.
 */

const EMAILJS_API_URL = "https://api.emailjs.com/api/v1.0/email/send";

const getEnv = (key, fallback = "") => {
  return (
    (typeof process !== "undefined" &&
      process.env &&
      process.env[key]) ||
    fallback
  );
};

const sendEmail = async ({ templateId, templateParams, captchaToken }) => {
  const serviceId = getEnv("REACT_APP_EMAILJS_SERVICE_ID");
  const publicKey = getEnv("REACT_APP_EMAILJS_PUBLIC_KEY");

  // If credentials are placeholder / missing in local dev, mock successful send
  if (
    !serviceId ||
    !publicKey ||
    publicKey === "your_emailjs_public_key" ||
    serviceId === "service_barbequeen"
  ) {
    console.info(
      "[EmailJS Service: DEV MODE] Email would be sent with payload:",
      {
        serviceId,
        templateId,
        templateParams: {
          ...templateParams,
          "g-recaptcha-response": captchaToken ? "CAPTCHA_VERIFIED" : undefined,
        },
      }
    );
    // Simulate brief network delay
    await new Promise((resolve) => setTimeout(resolve, 800));
    return { success: true, mock: true };
  }

  const payload = {
    service_id: serviceId,
    template_id: templateId,
    user_id: publicKey,
    template_params: {
      ...templateParams,
      "g-recaptcha-response": captchaToken || undefined,
      submission_time: new Date().toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
      }),
    },
  };

  const response = await fetch(EMAILJS_API_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || `EmailJS send error (${response.status})`);
  }

  return { success: true };
};

/**
 * Send Contact Message Form
 */
export const sendContactEmail = async (formData, captchaToken = "") => {
  const templateId = getEnv(
    "REACT_APP_EMAILJS_CONTACT_TEMPLATE_ID",
    "template_contact"
  );
  return sendEmail({
    templateId,
    templateParams: {
      from_name: formData.name,
      reply_to: formData.email,
      from_email: formData.email,
      phone_number: formData.mobile,
      message: formData.message,
      opt_in_sms: formData.agree ? "Yes" : "No",
      form_type: "Contact Us Inquiry",
    },
    captchaToken,
  });
};

/**
 * Send Franchise Application Form
 */
export const sendFranchiseEmail = async (formData, captchaToken = "") => {
  const templateId = getEnv(
    "REACT_APP_EMAILJS_FRANCHISE_TEMPLATE_ID",
    "template_franchise"
  );
  return sendEmail({
    templateId,
    templateParams: {
      from_name: formData.name,
      reply_to: formData.email,
      from_email: formData.email,
      phone_number: formData.mobile,
      occupation: formData.occupation || "N/A",
      location: formData.location || "N/A",
      experience: formData.experience || "N/A",
      role: formData.role || "N/A",
      property_status: formData.propertyStatus || "N/A",
      location_type: formData.locationType || "N/A",
      launch_date: formData.launchDate || "N/A",
      message: formData.message || "N/A",
      form_type: "Franchise Application",
    },
    captchaToken,
  });
};

/**
 * Send Table Reservation Form
 */
export const sendReservationEmail = async (formData, captchaToken = "") => {
  const templateId = getEnv(
    "REACT_APP_EMAILJS_RESERVATION_TEMPLATE_ID",
    "template_reservation"
  );
  return sendEmail({
    templateId,
    templateParams: {
      from_name: formData.name,
      reply_to: formData.email,
      from_email: formData.email,
      phone_number: formData.phone || formData.mobile,
      guests: formData.guests || formData.totalPersons || "N/A",
      date: formData.date || "N/A",
      time: formData.time || "N/A",
      outlet: formData.outlet || "Salem Main Outlet",
      dining_preference: formData.preference || "Indoor",
      occasion: formData.occasion || "Dining",
      special_request: formData.specialRequest || formData.message || "None",
      form_type: "Table Reservation",
    },
    captchaToken,
  });
};

const emailService = {
  sendContactEmail,
  sendFranchiseEmail,
  sendReservationEmail,
};

export default emailService;
